import { useState, useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import type { AgentApiCandidateFormsShow } from '@/types/serializers';
import type { AgentApiCandidateFormItemsShow } from '@/types/serializers';
import Preview from './Preview';
import EditCard from './EditCard';
import useReorderCandidateFormItems from '@/features/agent/form/api/useReorderCandidateFormItems';

interface Props {
  candidateForm: AgentApiCandidateFormsShow;
  selectedItemId?: number;
  onItemClick?: (itemId: number) => void;
  onAddItem?: () => void;
}

// order_numberが型定義に含まれていない場合の拡張型
type FormItemWithOrder = AgentApiCandidateFormItemsShow & {
  order_number?: number;
};

export default function Tab({ candidateForm, selectedItemId, onItemClick }: Props) {
  const [internalSelectedId, setInternalSelectedId] = useState<number | undefined>(selectedItemId);
  const [editedFormItem, setEditedFormItem] =
    useState<Partial<AgentApiCandidateFormItemsShow> | null>(null);
  const selectLastOnAddRef = useRef(false);
  const { mutateAsync: reorderItems } = useReorderCandidateFormItems();

  // form_itemsをorder_number:ascでソート
  const sortItems = (items: AgentApiCandidateFormItemsShow[]) => {
    return [...items].sort((a, b) => {
      const itemA = a as FormItemWithOrder;
      const itemB = b as FormItemWithOrder;
      const orderA = itemA.order_number ?? itemA.id;
      const orderB = itemB.order_number ?? itemB.id;
      return orderA - orderB;
    });
  };

  // ローカル状態で管理して、ドラッグエンド時に即座に更新（楽観的更新）
  const [localSortedItems, setLocalSortedItems] = useState<AgentApiCandidateFormItemsShow[]>(() =>
    sortItems(candidateForm.form_items || [])
  );

  // candidateFormが変更されたらlocalSortedItemsも更新
  useEffect(() => {
    setLocalSortedItems(sortItems(candidateForm.form_items || []));
  }, [candidateForm.form_items]);

  // 項目追加後に items の最後を選択する（onAddSuccess → refetch → localSortedItems 更新後に発火）
  useEffect(() => {
    if (!selectLastOnAddRef.current || localSortedItems.length === 0) return;
    const lastItem = localSortedItems[localSortedItems.length - 1];
    selectLastOnAddRef.current = false;
    setInternalSelectedId(lastItem.id);
    setEditedFormItem(null);
    onItemClick?.(lastItem.id);
  }, [localSortedItems, onItemClick]);

  const handleItemClick = (itemId: number) => {
    const currentSelectedId = selectedItemId !== undefined ? selectedItemId : internalSelectedId;
    // すでに選択中の項目を再クリックしても何もしない（選択解除はしない）
    if (currentSelectedId === itemId) return;

    setInternalSelectedId(itemId);
    setEditedFormItem(null); // アイテム変更時は編集状態をリセット
    onItemClick?.(itemId);
  };

  const handleAddSuccess = () => {
    selectLastOnAddRef.current = true;
  };

  const handleReorder = async (reorderedItems: AgentApiCandidateFormItemsShow[]) => {
    // 即座にローカル状態を更新（楽観的更新）
    setLocalSortedItems(reorderedItems);

    const ids = reorderedItems.map((item) => item.id);
    try {
      await reorderItems({ ids });
    } catch (error) {
      console.error('順序変更に失敗しました:', error);
      // エラー時は元の状態に戻す
      setLocalSortedItems(sortItems(candidateForm.form_items || []));
    }
  };

  const sortedItems = localSortedItems;

  // 初期化時に選択情報がない場合、最初のアイテムを選択
  useEffect(() => {
    if (
      selectedItemId === undefined &&
      internalSelectedId === undefined &&
      sortedItems.length > 0
    ) {
      const firstItemId = sortedItems[0].id;
      setInternalSelectedId(firstItemId);
      onItemClick?.(firstItemId);
    }
  }, [selectedItemId, sortedItems, onItemClick]);

  const currentSelectedId = selectedItemId !== undefined ? selectedItemId : internalSelectedId;
  const selectedItem = sortedItems.find((item) => item.id === currentSelectedId);

  // 編集されたアイテムと元のアイテムをマージ
  const previewItem =
    selectedItem && editedFormItem ? { ...selectedItem, ...editedFormItem } : selectedItem;

  // 選択アイテムが変更されたら編集状態をリセット
  useEffect(() => {
    setEditedFormItem(null);
  }, [currentSelectedId]);

  return (
    <div className="flex h-[calc(100vh-140px)] w-full gap-4 overflow-hidden">
      <div className="shrink-0">
        <Sidebar
          formItems={sortedItems}
          selectedItemId={currentSelectedId}
          onItemClick={handleItemClick}
          onReorder={handleReorder}
          onAddSuccess={handleAddSuccess}
        />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <Preview formItem={previewItem} questionPageUrl={candidateForm.question_page_url} />
        <EditCard
          formItem={selectedItem}
          candidateFormId={candidateForm.id}
          candidateForm={candidateForm}
          onItemDeleted={() => {
            // 削除後、選択を解除
            setInternalSelectedId(undefined);
            setEditedFormItem(null);
            onItemClick?.(undefined as unknown as number);
          }}
          onFormItemChange={(updatedItem) => {
            setEditedFormItem(updatedItem);
          }}
          onItemSaved={() => {
            // 保存成功後、編集状態をリセット（サーバーから最新データが取得される）
            setEditedFormItem(null);
          }}
        />
      </div>
    </div>
  );
}
